import { useEffect, useState } from "react";
import {
  useAlertStore,
  useGeneralStore,
  usePopupStore,
  useThemeStore,
  useUserStore,
} from "../../../stores";
import { FactorDetailsProps } from "./types";
import { Column, Row, ScrollRow } from "../../containers";
import { PopupBack, PopupClose, QuestionBox } from "../../../popup";
import { Heading } from "../../typography";
import { SelectorInput, TextInput } from "../../inputs";
import { Cheque, Customer, Vec2 } from "../../../types";
import { addComma, factorTypeToString } from "../../../utils";
import ActivityRenderer from "../../activity";
import { ActivityRendererType } from "../../activity/types";
import { LilCheque } from "../../cheque";
import { FactorApi } from "../../../api";
import OptionList from "../../optionlist";
import { EditIcon, TrashIcon } from "../../../assets/icons";
import UpdateSpentFactor from "../update-spent-factor";
import UpdateReceivedFactor from "../update-received-factor";

const FactorDetails = <T extends {}>(props: FactorDetailsProps<T>) => {
  const { factor, back, updateCb } = props;

  const [cheques, setCheques] = useState<Array<Cheque>>([]);

  const theme = useThemeStore((s) => s.theme);
  const setPopupProperties = usePopupStore((s) => s.setPopupProperties);
  const customers = useGeneralStore((s) => s.customers);
  const getCustomerById = useGeneralStore((s) => s.getCustomerById);
  const setPopupBody = usePopupStore((s) => s.setPopupBody);
  const pushAlert = useAlertStore((s) => s.pushAlert);
  const hasUserAccess = useUserStore((s) => s.hasUserAccess);

  useEffect(() => {
    setPopupProperties({
      style: {
        maxHeight: "80%",
        overflowY: "auto",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  useEffect(() => {
    FactorApi.getCheques(factor._id)
      .then((res) => {
        setCheques(res);
      })
      .catch((err) => {
        pushAlert(err.msg || "unknown error", "error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const [mousePos, setMousePos] = useState<Vec2>({ x: 0, y: 0 });

  const onRightClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void => {
    e.preventDefault();
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  return (
    <Column
      style={{ padding: "1rem", position: "relative" }}
      gap={15}
      props={{ onContextMenu: onRightClick }}
    >
      <PopupClose />

      {back ? (
        <PopupBack Component={back.component} props={back.props} />
      ) : (
        <></>
      )}

      <OptionList
        mousePos={mousePos}
        options={[
          {
            title: "ویرایش",
            Icon: EditIcon,
            onClick: () => {
              if (!hasUserAccess(["EditFactorCheque"]))
                return pushAlert("دسترسی غیر مجاز", "error");

              if (factor.type === "spent") {
                setPopupBody(
                  <UpdateSpentFactor<FactorDetailsProps<T>>
                    factor={factor}
                    updateCb={updateCb}
                    back={{ component: FactorDetails, props }}
                  />
                );
              } else {
                setPopupBody(
                  <UpdateReceivedFactor<FactorDetailsProps<T>>
                    factor={factor}
                    updateCb={updateCb}
                    back={{ component: FactorDetails, props }}
                  />
                );
              }
            },
          },
          {
            title: "حذف",
            Icon: TrashIcon,
            iconColor: theme.alert,
            color: theme.alert,
            onClick: () => {
              if (!hasUserAccess(["EditFactorCheque"]))
                return pushAlert("دسترسی غیر مجاز", "error");

              setPopupBody(
                <QuestionBox<FactorDetailsProps<T>>
                  question="آیا مطمئن هستید که می خواهید این فاکتور را حذف کنید؟"
                  close={false}
                  back={{ component: FactorDetails, props }}
                  onNo={() => setPopupBody(<FactorDetails {...props} />)}
                  onYes={() => {
                    FactorApi.deleteFactor(factor._id.$oid)
                      .then(() => {
                        setPopupBody(
                          back ? <back.component {...back.props} /> : null
                        );
                        if (updateCb) updateCb();
                        pushAlert("عملیات موفقیت آمیز");
                      })
                      .catch((err) => {
                        pushAlert(err.msg || "unknown error", "error");
                      });
                  }}
                />
              );
            },
          },
        ]}
      />

      <Heading color={theme.textOnOverlay} size={18}>
        {`اطلاعات فاکتور ${factorTypeToString(factor.type)}`}
      </Heading>

      <Row style={{ width: "100%" }} gap={20} className="factor-activity-row">
        <SelectorInput<Customer>
          items={customers}
          uniqueKey={"fullname"}
          selected={getCustomerById(factor.customer.$oid)}
          setSelected={() => {}}
          readOnly
          stringField={"fullname"}
          renderStrings={["fullname", "phone"]}
          compareFn={(c, v) =>
            c.fullname.toLowerCase().includes(v.toLowerCase()) ||
            c.phone.includes(v)
          }
          label="نام مشتری"
        />

        <TextInput
          label="بابت"
          value={factor.description}
          inputProps={{ readOnly: true }}
        />

        <TextInput
          label="مبلغ کل (ریال)"
          value={addComma(factor.total_price)}
          inputProps={{ readOnly: true }}
        />
      </Row>

      <Heading
        size={18}
        textStyles={{ width: "100%", textAlign: "right" }}
        color={theme.textOnOverlay}
      >
        رویداد های این فاکتور
      </Heading>
      <ScrollRow gap={20} height={300}>
        {factor.activities.map((activity, idx) => (
          <ActivityRenderer<FactorDetailsProps<T>>
            key={idx}
            activity={activity}
            type={ActivityRendererType.Small}
            back={{ component: FactorDetails, props }}
          />
        ))}
      </ScrollRow>

      <Heading
        textStyles={{ width: "100%", textAlign: "right" }}
        color={theme.textOnOverlay}
      >
        چک ها
      </Heading>
      <ScrollRow gap={10} height={200}>
        {cheques.map((c) => (
          <LilCheque<FactorDetailsProps<T>>
            key={c._id.$oid}
            {...c}
            background={theme.background}
            back={{ component: FactorDetails, props }}
            updateCb={updateCb}
          />
        ))}
      </ScrollRow>
    </Column>
  );
};

export default FactorDetails;
